import axiosInstance from './axiosConfig';

/**
 * 動画スタイルレシピ（商品×スタイルで自動生成 → 編集して作り直す）の API クライアント。
 *
 * 生成の進捗は一覧（GET /recipe-videos）か詳細（GET /recipe-videos/{id}）のポーリングだけで追う
 * （段ごとの n/m は stages に入っている。素材ごとに個別に取りに行かない）。
 * 編集（セリフ・字幕・絵や動画の作り直し）は台本に印を付けるだけで、apply で初めて作り直しが始まる。
 */

export type RecipeVideoStatus = 'pending' | 'running' | 'completed' | 'failed';

export type RecipeVideoStep =
  | 'script'
  | 'characters'
  | 'images'
  | 'voices'
  | 'bgm'
  | 'timing'
  | 'clips'
  | 'compose'
  | 'done';

/** 一覧・詳細の「作成の進み具合」の段（step のうち利用者に見せるもの。当てはまらない段はサーバーが出さない） */
export type RecipeVideoStageKey = 'script' | 'characters' | 'images' | 'voices' | 'bgm' | 'clips' | 'compose';

export type RecipeVideoStageState = 'done' | 'current' | 'pending' | 'failed';

/** 仕上げ（compose）の中の段階。compose の段にだけ付く */
export type RecipeVideoComposePhase = 'queued' | 'waiting' | 'frames' | 'segments' | 'audio' | 'upload';

export interface RecipeVideoStage {
  key: RecipeVideoStageKey;
  state: RecipeVideoStageState;
  /** 数が分からない段（台本前など）は total=null。これからの段（pending）は done=null */
  done: number | null;
  total: number | null;
  phase: RecipeVideoComposePhase | null;
}

export interface RecipeVideoStyle {
  id: number;
  slug: string;
  name: string;
  description: string;
  scene_count: number;
  approx_seconds: number;
  thumbnail_url: string | null;
  sample_video_url: string | null;
  estimated_credits: number;
}

export interface RecipeVideoAppeal {
  name: string;
  direction: string | null;
}

export interface RecipeVideoSummary {
  id: number;
  title: string;
  product_id: number;
  product_name: string | null;
  style_name: string | null;
  status: RecipeVideoStatus;
  step: RecipeVideoStep;
  progress: number;
  stages: RecipeVideoStage[];
  /** 失敗時だけ、利用者向けの短い文 */
  error: string | null;
  thumbnail_url: string | null;
  output_seconds: number | null;
  has_video: boolean;
  created_at: string | null;
  /** 今の作成（最初の作成・反映・やり直し）を始めた時刻。経過時間はここから数える */
  started_at: string | null;
  updated_at: string | null;
}

export interface RecipeVideoLine {
  speaker: string | null;
  kind: 'dialogue' | 'monologue' | 'narration' | null;
  text: string;
  caption_text: string | null;
}

export interface RecipeVideoScene {
  index: number;
  role: string | null;
  kind: 'manga_panel' | 'photo_clip' | 'talking_head' | 'product_ui' | 'endcard' | string;
  text_style: string | null;
  lines: RecipeVideoLine[];
  image_url: string | null;
  image_status: string | null;
  can_regenerate_image: boolean;
  image_instruction: string | null;
  clip_status: string | null;
  can_regenerate_clip: boolean;
  start_seconds: number | null;
  duration_seconds: number | null;
}

export interface RecipeVideoPendingChanges {
  dirty: boolean;
  images: number;
  voices: number;
  clips: number;
  estimated_credits: number;
}

export interface RecipeVideoDetail extends RecipeVideoSummary {
  appeal: string | null;
  video_url: string | null;
  download_url: string | null;
  editable: boolean;
  pending_changes: RecipeVideoPendingChanges | null;
  scenes: RecipeVideoScene[];
}

export interface RecipeVideoLineInput {
  text: string;
  caption_text?: string | null;
}

/** ポーリングを続ける状態（作っている最中） */
export const isRecipeVideoBusy = (status: RecipeVideoStatus): boolean => status === 'pending' || status === 'running';

/**
 * URL の一部にする ID・シーン番号は正の整数だけを通す（`../admin` のような値で別の API を叩かせない）
 */
export const parseRecipeVideoId = (value: unknown): number | null => {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : '';
  if (!/^\d{1,12}$/.test(text)) return null;
  const id = Number(text);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const requireId = (value: unknown): number => {
  const id = parseRecipeVideoId(value);
  if (id === null) throw new Error('不正な ID です');
  return id;
};

const requireIndex = (value: unknown): number => {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) throw new Error('不正なシーン番号です');
  return value;
};

/** 作成ボタン1回ごとのキー（再送・二重送信で同じプロジェクトを返してもらう） */
export const newIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

const extractErrorMessage = (error: any): string =>
  error?.response?.data?.message || error?.message || 'エラーが発生しました';

/** 402 credit_insufficient（開始・反映の前の残高確認で足りない） */
const isCreditInsufficientError = (error: any): boolean =>
  error?.response?.status === 402 || error?.response?.data?.error === 'credit_insufficient';

const recipeVideoService = {
  async getStyles(): Promise<RecipeVideoStyle[]> {
    const response = await axiosInstance.get('/recipe-videos/styles');
    return response.data.data;
  },

  async getAppeals(productId: number): Promise<RecipeVideoAppeal[]> {
    const response = await axiosInstance.get(`/recipe-videos/products/${requireId(productId)}/appeals`);
    return response.data.data;
  },

  async getProjects(): Promise<RecipeVideoSummary[]> {
    const response = await axiosInstance.get('/recipe-videos');
    return response.data.data;
  },

  async createProject(params: {
    product_id: number;
    video_style_recipe_id: number;
    appeal?: string;
    /** 商品選択モーダルの「誰に、何を伝えるか」で選んだ訴求の狙いと対象（任意） */
    appeal_direction?: string;
    appeal_segment?: string;
    idempotency_key?: string;
  }): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.post('/recipe-videos', params);
    return response.data.data;
  },

  async getProject(id: number | string): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.get(`/recipe-videos/${requireId(id)}`);
    return response.data.data;
  },

  async deleteProject(id: number | string): Promise<void> {
    await axiosInstance.delete(`/recipe-videos/${requireId(id)}`);
  },

  async updateScene(id: number | string, index: number, lines: RecipeVideoLineInput[]): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.patch(`/recipe-videos/${requireId(id)}/scenes/${requireIndex(index)}`, { lines });
    return response.data.data;
  },

  async regenerateImage(id: number | string, index: number, instruction?: string): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.post(`/recipe-videos/${requireId(id)}/scenes/${requireIndex(index)}/regenerate-image`, {
      instruction: instruction?.trim() || undefined,
    });
    return response.data.data;
  },

  async regenerateClip(id: number | string, index: number): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.post(`/recipe-videos/${requireId(id)}/scenes/${requireIndex(index)}/regenerate-clip`);
    return response.data.data;
  },

  async apply(id: number | string): Promise<RecipeVideoDetail> {
    const response = await axiosInstance.post(`/recipe-videos/${requireId(id)}/apply`);
    return response.data.data;
  },

  extractErrorMessage,
  isCreditInsufficientError,
};

export default recipeVideoService;
